<template>
    <div class="dashboard-container" :class="{ 'loading-state': loading && isFirstLoad }">
        <!-- Skeleton Loading (only on first load) -->
        <template v-if="loading && isFirstLoad">
            <SkeletonLoader type="dashboard" :count="3" />
        </template>

        <template v-else>
            <Desktop :dashboard="dashboard" v-if="isDesktop"></Desktop>
            <Mobile :dashboard="dashboard" v-else></Mobile>
        </template>
        <o-profile-completion-modal :isVisible="showProfileCompletionPopup"
          :corporateLogo="dashboard?.branch_images?.[0]?.business_profile?.logo?.image_url" :countries="countries"
          @close="closePhoneModal" @submit-phone="handlePhoneSubmit">
        </o-profile-completion-modal>
    </div>
</template>

<script>
import Desktop from './MemberDashboardDesktop.vue';
import OProfileCompletionModal from "@/components/OmnihControl/OProfileCompletionModal.vue";
import Mobile from './MemberDashboardMobile.vue';
import SkeletonLoader from '@/components/member/SkeletonLoader.vue';
import { onMounted, onBeforeUnmount, ref } from 'vue';
import { mapState } from 'vuex';
import { useStore } from 'vuex';
import { useTheme } from '@/composables/useTheme';
import { useModalHistory } from '@/composables/useModalHistory';

export default {
    name: 'MemberDashboard',
    components: {
        Desktop,
        Mobile,
        OProfileCompletionModal,
        SkeletonLoader
    },
    setup() {
        const store = useStore();
        const loading = ref(true);
        const showProfileCompletionPopup = ref(false);
        const countries = ref([]);
        const isFirstLoad = ref(true);

        const { pushModalHistory: pushPhoneModal, popModalHistory: popPhoneModal } = useModalHistory({
          isVisible: showProfileCompletionPopup,
          onClose: () => { showProfileCompletionPopup.value = false; },
          stateKey: 'phone-completion-modal',
        });

    const isDesktop = ref(typeof window !== 'undefined' ? window.innerWidth >= 992 : false);

    const updateViewport = () => {
        isDesktop.value = window.innerWidth >= 992;
    };

    onMounted(() => {
      updateViewport();
      window.addEventListener('resize', updateViewport);
      loadState();
    });

    const loadCountries = () => {
      return store.dispatch("Countries/read")
        .then((response) => {
          countries.value = response.map((country) => ({
            value: country.id,
            text: country.name,
            phone_code: country.phone_code, // Include phone_code
          }));
        })
        .catch((err) => {
          console.error("Failed to load countries:", err);
          throw err; 
        });
    };

    const loadState = () => {
      store
        .dispatch("ClientDashboard/read")
        .then(() => {
          return loadCountries();
        })
        .then(() => {
          const corporateId =
            store.state.ClientDashboard.dashboard.corporate_id;
          return fetchTheme(corporateId);
        })
        .catch((err) => {
          console.error("Failed to load dashboard data or fetch state:", err)
        })
        .finally(() => {
          loading.value = false;
          isFirstLoad.value = false;
        });
    };

    const fetchTheme = (corporateId) => {
      if (corporateId) {
        const { applyTheme } = useTheme();
        return applyTheme(corporateId);
      }
    };

    const closePhoneModal = () => {
      showProfileCompletionPopup.value = false;
      popPhoneModal();
    };

    const openPhoneModal = () => {
      if (showProfileCompletionPopup.value) return; // already open, don't double-push
      showProfileCompletionPopup.value = true;
      pushPhoneModal();
    };

    // LOGIC FOR HANDLING DATA CHANGES FROM THE MODAL
    const handlePhoneSubmit = (payload) => {

      const updatePayload = {
        id: store.state.Auth.currUser.id, 
        phone: payload.phone,
        country: payload.country
      };

      store
      .dispatch("Member/updateProfile", updatePayload)
      .then(() => {
          showProfileCompletionPopup.value = false;
          loadState(); 
        })
        .catch((err) => {
          console.error("Failed to update phone number:", err);
          const errorMsg = err.response?.data?.message || "Please check your number again or try again.";
          alert(`Failed to save phone number. ${errorMsg}`);
        });
    };

    onBeforeUnmount(() => {
      window.removeEventListener('resize', updateViewport);
    });

    return {
      loading,
      isFirstLoad,
      fetchTheme,
      showProfileCompletionPopup,
      handlePhoneSubmit,
      closePhoneModal,
      openPhoneModal,
      countries,
      isDesktop,
    };
  },
  computed: {
    ...mapState({
      dashboard: state => state.ClientDashboard.dashboard,
    }),
  },
  watch: {
    dashboard: {
      handler(newVal) {
        if (newVal && newVal.member_profile) {
          const phone = newVal.member_profile.phone;
          if (phone === null || phone === "+6281234567890" || phone === "+62 81234567890" || phone === "+62 81-234-567-890" || phone === "") {
            this.$nextTick(() => this.openPhoneModal());
          }
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
@media (max-width: 991px) {
  .dashboard-container.loading-state {
    overflow: hidden;
    position: fixed;
    width: 100%;
    height: 100vh;
    touch-action: none;
    overscroll-behavior: none;
    -webkit-overflow-scrolling: auto;
  }
}
</style>